import { CDMO_STATUS, type CDMO_ENUM } from '../constants';
import type { CDMOApplication } from './supabase';

/**
 * Claim credentials for a new profile. Generated by the caller because they are non-deterministic,
 * and only ever present on an admin-created profile that nobody owns yet.
 */
export interface CdmoApplicationClaimTokens {
  readonly claimToken: string | null;
  readonly adminClaimToken: string | null;
  readonly claimTokenExpiresAt: string | null;
}

/**
 * Profile fields the create path deliberately does not write: the database supplies the first two,
 * and the campaign columns belong to the claim-email flow, which sets them long after intake.
 */
type ColumnNotWrittenOnCreate =
  | 'id'
  | 'created_at'
  | 'campaign_active'
  | 'claim_email_stage'
  | 'last_email_sent_at'
  | 'claim_campaign_started_at';

/**
 * Every column written when a CDMO profile is created, each one required.
 *
 * Mandatory keys are the whole point. A column the form collects but the insert omits is lost in
 * silence — the insert succeeds, the row appears, and the field is simply absent on the next load.
 * Because nothing here is optional, dropping a column fails the build rather than shipping, and a
 * field added to {@link CDMOApplication} stays a build failure until it is either written below or
 * named in {@link ColumnNotWrittenOnCreate} as deliberately excluded.
 */
export type CdmoApplicationRow = {
  [K in Exclude<keyof CDMOApplication, ColumnNotWrittenOnCreate>]-?: CDMOApplication[K] | null;
};

/**
 * The `normalized_domain` stored for a website: lower-cased hostname without `www.`. Exported so the
 * CSV bulk upload's duplicate check compares domains exactly the way they are written.
 */
export function normalizeWebsiteDomain(website: string | undefined): string | null {
  if (!website) return null;
  try {
    // The scheme is case-insensitive: "Https://x.com" already has one, and prefixing another would
    // make the hostname "https".
    const url = /^https?:/i.test(website) ? website : `https://${website}`;
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch (e) {
    console.warn('Failed to normalize domain:', e);
    return null;
  }
}

function resolveInitialStatus(data: CDMOApplication): CDMO_ENUM {
  if (data.status) return data.status;
  return data.user_id ? CDMO_STATUS.CLAIMED : CDMO_STATUS.UNCLAIMED;
}

/** The row written for a brand new CDMO profile. */
export function buildCdmoApplicationRow(
  data: CDMOApplication,
  tokens: CdmoApplicationClaimTokens
): CdmoApplicationRow {
  return {
    user_id: data.user_id ?? null,
    company_name: data.company_name,
    contact_name: data.contact_name,
    email: data.email,
    phone: data.phone,
    website: data.website || null,
    fei_number: data.fei_number || null,
    primary_capabilities: data.primary_capabilities,
    detailed_capabilities: data.detailed_capabilities,
    facilities: data.facilities ?? [],
    typical_batch_sizes: data.typical_batch_sizes,
    target_client_types: data.target_client_types,
    engagement_scales: data.engagement_scales,
    preferred_project_types: data.preferred_project_types,
    gmp_certifications: data.gmp_certifications,
    facility_locations_details: data.facility_locations_details || null,
    budget_ranges: data.budget_ranges || [],
    operating_regions: data.operating_regions || [],
    speed_to_start: data.speed_to_start || null,
    willingness_feasibility_studies: data.willingness_feasibility_studies,
    startup_friendly: data.startup_friendly,
    description: data.description || null,
    file_name: data.file_name || null,
    status: resolveInitialStatus(data),
    cdmo_business_profile: data.cdmo_business_profile || 'Mid-Size Specialist',
    primary_email: data.primary_email || null,
    secondary_emails: data.secondary_emails || [],
    admin_notes: data.admin_notes || null,
    normalized_domain: normalizeWebsiteDomain(data.website),
    is_admin_created: data.is_admin_created || false,
    claim_token: tokens.claimToken,
    admin_claim_token: tokens.adminClaimToken,
    claim_token_expires_at: tokens.claimTokenExpiresAt,
    locked_fields: data.locked_fields ?? [],
  };
}
